import { z } from 'zod';
import { amount, Id } from './common.js';

/**
 * Vague 7 : **chiffres de l'atelier** (`metrics.atelier`, droit `amounts.view`, module `atelier_metrics`) et
 * **bilan hebdomadaire** du propriétaire (module `weekly_digest`). Calculés par le cœur
 * (`core/services/atelier-metrics.ts`) **à partir de nos données**, en `numeric` (jamais en flottant), sur
 * les jours locaux du garage (fuseau de ses réglages). Jamais de facture ni d'encaissement : Pennylane fait
 * foi ; les encaissements seront relus depuis Pennylane (place `invoicing`, `null` aujourd'hui).
 */

const Count = z.int().nonnegative();

/**
 * Chiffres d'une période `[from, to]` (jours locaux, bornes incluses) :
 * - `complementsAccepted` : décisions « accepté » horodatées dans la période (nombre, montant TTC de la
 *   version acceptée) ;
 * - `complementsRefused` : décisions « refusé » dans la période ;
 * - `decided` = acceptés + refusés (le report et la demande de modification ne sont pas des décisions) ;
 * - `acceptanceRatePercent` : acceptés / décidés × 100, arrondi à 1 décimale (chaîne décimale), `null` sans
 *   décision ;
 * - `medianDecisionMinutes` : médiane (valeur réelle du milieu, `percentile_disc`) du délai entre l'envoi de
 *   la version et la décision, en minutes entières, sur les décisions acceptées ou refusées de la période ;
 * - `workDone` : travaux réalisés (`work_items.performed_at` dans la période) ; montant TTC des lignes
 *   chiffrées (arrondi au centime par ligne, comme `lineAmounts`), nombre de lignes et lignes sans prix ;
 * - `vehiclesReleased` : véhicules sortis (`released_at` dans la période) ;
 * - `vehiclesReceived` : véhicules entrés (`received_at` dans la période).
 */
export const AtelierFigures = z.object({
  complementsAccepted: z.object({ count: Count, amountTtc: amount() }),
  complementsRefused: z.object({ count: Count, amountTtc: amount() }),
  decided: Count,
  acceptanceRatePercent: z.string().regex(/^\d{1,3}\.\d$/).nullable(),
  medianDecisionMinutes: z.int().nonnegative().nullable(),
  workDone: z.object({ lines: Count, unpricedLines: Count, amountTtc: amount() }),
  vehiclesReceived: Count,
  vehiclesReleased: Count,
});
export type AtelierFigures = z.infer<typeof AtelierFigures>;

const DateKey = z.iso.date();
export const MonthKey = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois au format AAAA-MM attendu');

export const AtelierPeriod = z.object({ from: DateKey, to: DateKey, figures: AtelierFigures });
export type AtelierPeriod = z.infer<typeof AtelierPeriod>;

/** `month` : mois affiché (défaut : mois en cours, fuseau du garage) ; jamais dans le futur (422). */
export const AtelierMetricsQuery = z.strictObject({ month: MonthKey.optional() });

export const AtelierMetricsOutput = z.object({
  timezone: z.string(),
  /** Mois demandé (en cours : jusqu'à aujourd'hui inclus). */
  month: z.object({ key: MonthKey }).extend(AtelierPeriod.shape),
  /** Mois précédent complet (comparaison). */
  previousMonth: z.object({ key: MonthKey }).extend(AtelierPeriod.shape),
  /** 12 mois glissants, le plus ancien d'abord, le mois demandé en dernier (courbe). */
  last12Months: z.array(z.object({ key: MonthKey }).extend(AtelierPeriod.shape)).length(12),
  /**
   * En attente de décision **maintenant** : versions envoyées sans décision définitive (statut `sent`),
   * nombre et montant TTC de la version en cours.
   */
  pendingDecision: z.object({ count: Count, amountTtc: amount() }),
  /**
   * Place prévue pour les **encaissements relus depuis Pennylane** (lecture seule, connecteur à venir).
   * Toujours `null` en vague 7 : aucune facture ni encaissement n'est calculé par l'outil.
   */
  invoicing: z.null(),
  generatedAt: z.iso.datetime({ offset: true }),
});
export type AtelierMetricsOutput = z.infer<typeof AtelierMetricsOutput>;

/**
 * Contenu d'un bilan hebdomadaire (`weekly_digests.content`), figé au calcul (lundi 8 h, heure du garage)
 * pour la semaine précédente (lundi → dimanche). Nombres et montants seulement : aucun nom, aucune plaque.
 * Envoyé aux **propriétaires actifs** du garage (adresse de leur compte) par L4.
 */
export const WeeklyDigestContent = z.object({
  weekStart: DateKey,
  weekEnd: DateKey,
  timezone: z.string(),
  figures: AtelierFigures,
  complements: z.object({
    accepted: Count,
    refused: Count,
    /** Versions envoyées encore sans décision au moment du calcul. */
    pending: Count,
    pendingAmountTtc: amount(),
  }),
  /** Messages du client non traités au moment du calcul. */
  unansweredMessages: Count,
  /** Véhicules prêts dont le délai gratuit est dépassé au moment du calcul (module gardiennage actif). */
  storageOverdue: Count,
});
export type WeeklyDigestContent = z.infer<typeof WeeklyDigestContent>;

/**
 * Règle « semaine sans activité » (rien n'est envoyé) : aucune entrée, aucune sortie, aucun travail réalisé,
 * aucune version envoyée, aucune décision et aucun message du client pendant la semaine.
 */
export const WeeklyDigestActivity = z.object({
  received: Count,
  released: Count,
  workLines: Count,
  complementsSent: Count,
  decisions: Count,
  customerMessages: Count,
});
export type WeeklyDigestActivity = z.infer<typeof WeeklyDigestActivity>;

export function weeklyDigestHasActivity(a: WeeklyDigestActivity): boolean {
  return a.received + a.released + a.workLines + a.complementsSent + a.decisions + a.customerMessages > 0;
}

/** Charge utile de l'événement `digest.weekly` : identifiant du bilan (contenu relu dans `weekly_digests`). */
export const WeeklyDigestRef = z.strictObject({ weeklyDigestId: Id, weekStart: DateKey });
